import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { SlideLessonPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._context";
import { useLearnCardActionController } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-action";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { showErrorToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";

/**
 * Marks the current slide complete, then advances. Shared by the default continue button
 * and any bespoke footer affordance that should leave the slide (e.g. the welcome slide's
 * "Continue without an account" link).
 *
 * The completion is marked BEFORE the destination is resolved (the controller's `continue` action
 * on a slide snapshot calls `completeSlide`): the pending-key buffer lets the resolver observe it
 * immediately, while the `user_slide` row is written behind. Where the flow goes next — the next
 * slide in authored order, a unit-boundary review, the next lesson, or out of the stack — is the
 * shared destination resolver's slide branch.
 */
export function useSlideLessonContinueMutation() {
  const [slideSlug, onContinue] = useContextSelectors(
    SlideLessonPageContext,
    (c) => c.slideSlug,
    (c) => c.onContinue,
  );
  const controller = useLearnCardActionController();
  const reportSlideContinueError = (error: unknown) => {
    logger(import.meta.location).error("Slide continue action failed", error);
    showErrorToast("Could not continue", error);
  };

  return useMutation({
    mutationFn: async () => {
      if (onContinue !== undefined) {
        onContinue();
        return;
      }
      await controller.submit({
        action: { kind: "continue" },
        // Slides read the bundled catalogue synchronously. A normal push keeps only the active
        // slide mounted, which keeps Android's accessibility hierarchy small while Maestro is
        // polling it.
        mode: "push",
        snapshot: { kind: "slide", slideSlug },
      });
    },
    mutationKey: ["slideLessonContinue"],
    onError: reportSlideContinueError,
  });
}
